
import { useState } from "react";
import { BusinessCategoryString } from "@/types/business";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { submitBusiness } from "@/services/business/submissionService";
import { validateBusinessForm } from "@/utils/formValidation";
import { useFormState } from "./forms/useFormState";
import { useFileHandler } from "./forms/useFileHandler";
import { FormData } from "./forms/types";

export const useSubmitForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { formData, formErrors, isSubmitting, setFormData, setFormError, clearFormError, setIsSubmitting, resetForm } = useFormState();
  const { handleMediaChange: fileHandler } = useFileHandler();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ [name]: value });
    
    if (formErrors[name]) {
      clearFormError(name);
    }
  };

  const handleCategoryChange = (value: string) => {
    setFormData({ category: value as BusinessCategoryString });
    
    if (formErrors.category) {
      clearFormError("category");
    }
  };

  const handleMediaChange = (files: FileList) => {
    fileHandler(files, formData.mediaFiles, (updatedFiles) => {
      setFormData({ mediaFiles: updatedFiles });
      
      if (formErrors.mediaFiles) {
        clearFormError("mediaFiles");
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Form Submission Started", { formData });

    const validation = validateBusinessForm(formData);

    if (!validation.isValid) {
      console.error("Form Validation Failed", validation.errors);
      
      // Set all validation errors in the form state
      Object.entries(validation.errors).forEach(([field, message]) => {
        setFormError(field, message);
      });

      toast({
        title: "Validation Error",
        description: "Please check the form and try again",
        variant: "destructive",
      });

      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Preparing Business Submission", {
        name: formData.name,
        category: formData.category,
        mediaFiles: formData.mediaFiles?.length || 0,
      });

      const result = await submitBusiness({
        name: formData.name,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        county: formData.county,
        category: formData.category,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        website: formData.website || null,
        phone: formData.phone || null,
        email: formData.email || null,
        mediaFiles:
          formData.mediaFiles.length > 0 ? formData.mediaFiles : undefined,
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        tiktok: formData.tiktok || null,
        region: formData.region || null,
        postcode: formData.postcode || null,
        what3words: formData.what3words || null,
        approved: false,
      });

      console.log("Submission Successful", result);

      toast({
        title: "Success",
        description: "Business listing submitted successfully",
      });

      resetForm();
      navigate("/");
    } catch (error) {
      console.error("Submission Error", error);

      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";

      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    formErrors,
    isSubmitting,
    handleChange,
    handleCategoryChange,
    handleMediaChange,
    resetForm,
    toast,
    navigate,
    handleSubmit,
  };
};
